import { Box, Stack, Link, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { pxToRem } from '@/@mui/utils/px-to-rem';
import { ContentBox } from '@/components/shared/content-box';
import { NotFound } from '@/components/shared/not-found';
import { PageContainer } from '@/components/shared/page-container';
import { PageSkeleton } from '@/components/shared/page-skeleton';
import { VideoPlayer } from '@/components/shared/video-player';
import { ExternalUrls } from '@/constants';
import { useResourceShow } from '@/data/resource-catalog';
import { useSetTitle } from '@/hooks/use-set-title';

export const VideoLibrary = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const { video, isFetching } = useResourceShow({ videoId: videoId || '' });

  useSetTitle(video?.revisioned_title || 'Biblioteca de Vídeos');

  const notFound = !video && !isFetching;

  if (isFetching) return <PageSkeleton />;

  return (
    <PageContainer>
      {notFound && (
        <Box py={4}>
          <NotFound
            title="Vídeo não encontrado."
            description="Este vídeo não está disponível no momento. Que tal explorar outros vídeos?"
            buttonText="Ir para home"
            href="/"
            centered
          />
        </Box>
      )}
      {!isFetching && video && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ pt: 3 }}
        >
          <VideoPlayer url={video.video_url} />
          <ContentBox
            maxWidth={{ xs: '100%', md: 400 }}
            maxHeight={{ xs: 'auto', md: 450 }}
            overflow="hidden"
          >
            <Typography variant="h2" mb={2}>
              {video.revisioned_title}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography>{video.channel_title}</Typography>•
              <Typography>
                {Math.floor(video.duration_in_seconds / 60)}:
                {String(video.duration_in_seconds % 60).padStart(2, '0')}
              </Typography>
              •
              <Typography>
                {new Date(video?.published_at || '')
                  .toLocaleDateString('pt-BR', {
                    month: 'short',
                    year: 'numeric',
                  })
                  .replace(' de ', ' ')}
              </Typography>
            </Stack>
            <Typography>
              {video.view_count?.toLocaleString()} visualizações
            </Typography>
            <Box sx={{ overflowY: 'auto', mt: 2 }}>
              <Typography variant="body1" whiteSpace="pre-line">
                {video.revisioned_description}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={3}>
                Algumas destas informações foram geradas por inteligência
                artificial. Se vir algo errado,{' '}
                <Link
                  sx={{ fontSize: pxToRem(13) }}
                  href={ExternalUrls.Support}
                  target="_blank"
                >
                  nos avise
                </Link>
                .
              </Typography>
            </Box>
          </ContentBox>
        </Stack>
      )}
    </PageContainer>
  );
};
